import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Hidden,
  PageBlock,
  Spread,
  Stack,
  Text,
  TextLinkButton,
  TooltipRenderer,
} from 'braid-design-system';
import { useLocation } from 'react-router';
import { SmartTextLink } from 'scoobie';

import { useLazyColorMode } from '../ColorModeProvider/ColorModeProvider.tsx';

import { Countdown } from './components/Countdown.tsx';

import { useLogin, usePermissions } from '#src/hooks/auth.tsx';
import { useEnvironmentConfig } from '#src/hooks/environment.tsx';
import { useSelf } from '#src/hooks/self.ts';
import { useOrganizations } from '#src/pages/manage/organizations/OrganizationContext.tsx';

export const ImpersonationBanner = () => {
  const { baseUrls, auth } = useEnvironmentConfig();
  const location = useLocation();

  const { self } = useSelf();

  const { isImpersonating, integrationPartnerUuid } = usePermissions();

  const lazyColorMode = useLazyColorMode();

  const { organizations } = useOrganizations();
  const impersonatingOrganization = organizations.find(
    (org) => org.integrationPartnerUuid === integrationPartnerUuid,
  );
  const { getAccessTokenSilently } = useAuth0();
  const { login } = useLogin();
  if (!location.pathname.startsWith('/manage')) {
    // TODO: This heuristic isn't perfect, as we have ambitions to incrementally enhance the logged-in experience of the primary documentation.
    // However, most of that is probably going to be read-only anyway?
    return null;
  }

  return isImpersonating ? (
    <Box
      background={
        lazyColorMode?.colorMode === 'darkMode' ? 'caution' : 'cautionLight'
      }
    >
      <PageBlock width="large">
        <Box paddingY="medium">
          <Spread space="none" alignY="bottom">
            <Stack space="medium">
              <Text>
                You are currently impersonating {self?.partner.name}.
                <Hidden below="desktop">
                  {' '}
                  Refrain from editing their settings.
                </Hidden>
              </Text>

              <Text>
                <SmartTextLink
                  href={`${baseUrls.backoffice}/indirect-relationships?orgType=RSP`}
                >
                  Change in the Back Office
                </SmartTextLink>{' '}
                or{' '}
                <TextLinkButton
                  onClick={async () => {
                    await login({
                      organization: auth.internalTestPartnerOrganizationId,
                    });

                    getAccessTokenSilently({
                      cacheMode: 'off',
                    });
                  }}
                >
                  reset
                </TextLinkButton>
                .
              </Text>
            </Stack>

            {impersonatingOrganization ? (
              <TooltipRenderer
                id="impersonation-banner-expiry-tooltip"
                tooltip={
                  <Text size="xsmall">
                    Time remaining until impersonation session expires
                  </Text>
                }
              >
                {({ triggerProps }) => (
                  <Box {...triggerProps}>
                    <Countdown
                      endTime={
                        impersonatingOrganization.impersonationExpiry
                          ? new Date(
                              impersonatingOrganization.impersonationExpiry,
                            )
                          : new Date()
                      }
                    />
                  </Box>
                )}
              </TooltipRenderer>
            ) : null}
          </Spread>
        </Box>
      </PageBlock>
    </Box>
  ) : null;
};
